import { useContext } from 'react'
import { VendorsFetchContext } from 'vendors/api/VendorsFetchContext'
import { AxiosResponse } from 'axios'

type UseVendorsApiClientReturnType = {
  getWorkRegions: () => Promise<WorkRegionsResponseType[]>
  checkRepresentationRights: (payload: CheckRepresentationRightsRequestType) => Promise<boolean>
  ensurementRequest: (payload: EnsurementRequestType) => Promise<void>
  getMe: () => Promise<UserResponseType>
  getVendorDetailsByBusinessId: (businessId: string) => Promise<VendorResponseType>
  getVendorDetailsById: (vendorId: string) => Promise<VendorResponseType>
  getIsVendorFlowCompleted: () => Promise<boolean>
  completeVendorFlow: () => Promise<void>
  addUserContact: (contact: AddVendorContactRequestType, vendorId: string) => Promise<void>
  addGeneralContact: (contact: AddGeneralContactRequestType, vendorId: string) => Promise<void>
  getVendorContactsById: (vendorId: string) => Promise<VendorContactsResponseType>
  updateUserDetails: (payload: UpdateUserDetailsRequestType) => Promise<void>
  getServiceClassifications: () => Promise<ServiceClassificationResponseType[]>
  userContactReceivesRfps: (vendorId: string, userId: string) => Promise<void>
  userContactDoesNotReceiveRfps: (vendorId: string, userId: string) => Promise<void>
  getUserContact: (vendorId: string, userId: string) => Promise<VendorUserContactGetType>
  generalContactReceivesRfps: (vendorId: string, contactId: string) => Promise<void>
  generalContactDoesNotReceiveRfps: (vendorId: string, contactId: string) => Promise<void>
  updateUserContact: (vendorId: string, contactId: string, payload: UpdateUserContactRequestType) => Promise<void>
  deleteUserContact: (vendorId: string, contactId: string) => Promise<void>
  deleteGeneralContact: (vendorId: string, contactId: string) => Promise<void>
  getVendorGeneralContact: (vendorId: string, generalContactId: string) => Promise<GeneralContactResponseType>
  addVendor: (payload: AddVendorRequestType) => Promise<string>
  updateGeneralContact: (
    vendorId: string,
    generalContactId: string,
    payload: UpdateGeneralContactRequestType
  ) => Promise<UpdateGeneralContactRequestType>
  getWorkRegionsByVendorId: (vendorId: string) => Promise<VendorWorkRegion[]>
  setWorkRegionForVendorId: (vendorId: string, workRegionId: string) => Promise<void>
  deleteWorkRegionFromVendorId: (vendorId: string, workRegionId: string) => Promise<void>
  getUserVendors: () => Promise<UserVendor[]>
  setServiceClassificationForVendorId: (vendorId: string, classificationId: string) => Promise<void>
  deleteServiceClassificationForVendorId: (vendorId: string, classificationId: string) => Promise<void>
  getServiceClassificationsByVendorId: (vendorId: string) => Promise<VendorServiceClassification[]>
  getHistoryTimelineByVendorId: (vendorId: string) => Promise<VendorHistoryTimeline[]>
  getOpenRequestForProposals: () => Promise<VendorUserRequestForProposal[]>
  getAvailableToModifyRequestForProposals: () => Promise<AvailableToModifyRequestForProposals[]>
  getUserProposalsHistory: () => Promise<ProposalHistory[]>
  updateVendorBasicDetails: (vendorId: string, payload: UpdateVendorDetailsRequest) => Promise<void>
  getProposalById: (proposalId: string) => Promise<ProposalMeResponseType>
  updateProposal: (proposalId: string, payload: ProposalUpdateRequestType) => Promise<void>
  getRequestForProposalPdf: (requestForProposalId: string) => Promise<AxiosResponse<string>>
  getProposalPdf: (proposalId: string) => Promise<AxiosResponse<string>>
  getCompanyByBusinessId: (businessId: string, region: string) => Promise<CompanyResponseType>
}

export const useVendorsApiClient = (): UseVendorsApiClientReturnType => {
  const { vendorsAxios } = useContext(VendorsFetchContext)

  const getWorkRegions = (): Promise<WorkRegionsResponseType[]> =>
    vendorsAxios.get(`/vendors/workregions`).then((res) => res.data)

  const checkRepresentationRights = (payload: CheckRepresentationRightsRequestType): Promise<boolean> =>
    vendorsAxios
      .post(`vendors/representation-rights/check`, payload, {
        timeout: 15_000
      })
      .then((response) => response.data)

  const ensurementRequest = (payload: EnsurementRequestType): Promise<void> =>
    vendorsAxios.post(`vendors/representation-rights/ensurement-request`, payload)

  const getMe = (): Promise<UserResponseType> => vendorsAxios.get(`/vendors/users/me`).then((res) => res.data)

  const getVendorDetailsByBusinessId = (businessId: string): Promise<VendorResponseType> =>
    vendorsAxios.get(`/vendors/businessId/${businessId}/details`).then((res) => res.data)

  const getVendorDetailsById = (vendorId: string): Promise<VendorResponseType> =>
    vendorsAxios.get(`/vendors/${vendorId}/details`).then((res) => res.data)

  const getIsVendorFlowCompleted = (): Promise<boolean> =>
    vendorsAxios.get(`/vendors/users/vendor-flow/is-completed`).then((res) => res.data)

  const completeVendorFlow = (): Promise<void> => vendorsAxios.post(`vendors/users/vendor-flow/complete`)

  const addUserContact = (contact: AddVendorContactRequestType, vendorId: string): Promise<void> =>
    vendorsAxios.post(`vendors/${vendorId}/user-contact`, contact)

  const addGeneralContact = (contact: AddGeneralContactRequestType, vendorId: string): Promise<void> =>
    vendorsAxios.post(`/vendors/${vendorId}/general-contact`, contact)

  const getVendorContactsById = (vendorId: string): Promise<VendorContactsResponseType> =>
    vendorsAxios.get(`/vendors/${vendorId}/contacts`).then((res) => res.data)

  const updateUserDetails = (payload: UpdateUserDetailsRequestType): Promise<void> =>
    vendorsAxios.put(`/vendors/users/details`, payload)

  const getServiceClassifications = (): Promise<ServiceClassificationResponseType[]> =>
    vendorsAxios.get(`/vendors/service-classifications`).then((res) => res.data)

  const userContactReceivesRfps = (vendorId: string, userId: string): Promise<void> =>
    vendorsAxios.put(`/vendors/${vendorId}/user-contact/${userId}/ReceivesRequestForProposals`)

  const userContactDoesNotReceiveRfps = (vendorId: string, userId: string): Promise<void> =>
    vendorsAxios.put(`/vendors/${vendorId}/user-contact/${userId}/DoesNotReceiveRequestForProposals`)

  const getUserContact = (vendorId: string, userId: string): Promise<VendorUserContactGetType> =>
    vendorsAxios.get(`vendors/${vendorId}/user-contact/${userId}`).then((res) => res.data)

  const generalContactReceivesRfps = (vendorId: string, contactId: string): Promise<void> =>
    vendorsAxios.put(`/vendors/${vendorId}/general-contact/${contactId}/ReceivesRequestForProposals`)

  const generalContactDoesNotReceiveRfps = (vendorId: string, contactId: string): Promise<void> =>
    vendorsAxios.put(`/vendors/${vendorId}/general-contact/${contactId}/DoesNotReceiveRequestForProposals`)

  const updateUserContact = (vendorId: string, userId: string, payload: UpdateUserContactRequestType): Promise<void> =>
    vendorsAxios.put(`/vendors/${vendorId}/user-contact/${userId}`, payload)

  const deleteUserContact = (vendorId: string, contactId: string): Promise<void> =>
    vendorsAxios.delete(`vendors/${vendorId}/user-contact/${contactId}`)

  const deleteGeneralContact = (vendorId: string, contactId: string): Promise<void> =>
    vendorsAxios.delete(`vendors/${vendorId}/general-contact/${contactId}`)

  const getVendorGeneralContact = (vendorId: string, generalContactId: string): Promise<GeneralContactResponseType> =>
    vendorsAxios.get(`/vendors/${vendorId}/general-contact/${generalContactId}`).then((res) => res.data)

  const addVendor = (payload: AddVendorRequestType): Promise<string> =>
    vendorsAxios.post(`/vendors`, payload).then((res) => res.data)

  const updateGeneralContact = (
    vendorId: string,
    generalContactId: string,
    payload: UpdateGeneralContactRequestType
  ): Promise<UpdateGeneralContactRequestType> =>
    vendorsAxios.put(`/vendors/${vendorId}/general-contact/${generalContactId}`, payload)

  const getWorkRegionsByVendorId = (vendorId: string): Promise<VendorWorkRegion[]> =>
    vendorsAxios.get(`/vendors/${vendorId}/workregions`).then((res) => res.data)

  const setWorkRegionForVendorId = (vendorId: string, workRegionId: string): Promise<void> =>
    vendorsAxios.post(`/vendors/${vendorId}/workregions/${workRegionId}`)

  const deleteWorkRegionFromVendorId = (vendorId: string, workRegionId: string): Promise<void> =>
    vendorsAxios.delete(`/vendors/${vendorId}/workregions/${workRegionId}`)

  const getUserVendors = (): Promise<UserVendor[]> => vendorsAxios.get(`/vendors/me`).then((res) => res.data)

  const setServiceClassificationForVendorId = (vendorId: string, classificationId: string): Promise<void> =>
    vendorsAxios.post(`/vendors/${vendorId}/service-classifications/${classificationId}`).then((res) => res.data)

  const deleteServiceClassificationForVendorId = (vendorId: string, classificationId: string): Promise<void> =>
    vendorsAxios.delete(`/vendors/${vendorId}/service-classifications/${classificationId}`).then((res) => res.data)

  const getServiceClassificationsByVendorId = (vendorId: string): Promise<VendorServiceClassification[]> =>
    vendorsAxios.get(`/vendors/${vendorId}/service-classifications`).then((res) => res.data)

  const getHistoryTimelineByVendorId = (vendorId: string): Promise<VendorHistoryTimeline[]> =>
    vendorsAxios.get(`/vendors/${vendorId}/timelines`).then((res) => res.data)

  const getOpenRequestForProposals = (): Promise<VendorUserRequestForProposal[]> =>
    vendorsAxios.get(`vendors/users/me/request-for-proposals/open`).then((res) => res.data)

  const getAvailableToModifyRequestForProposals = (): Promise<AvailableToModifyRequestForProposals[]> =>
    vendorsAxios.get(`vendors/users/me/request-for-proposals/available-to-modify`).then((res) => res.data)

  const getUserProposalsHistory = (): Promise<ProposalHistory[]> =>
    vendorsAxios.get(`vendors/users/me/proposals/history`).then((res) => res.data)

  const updateVendorBasicDetails = (vendorId: string, payload: UpdateVendorDetailsRequest): Promise<void> =>
    vendorsAxios.put(`vendors/${vendorId}`, payload)

  const getProposalById = (proposalId: string): Promise<ProposalMeResponseType> =>
    vendorsAxios.get(`vendors/me/proposals/${proposalId}`).then((res) => res.data)

  const updateProposal = (proposalId: string, payload: ProposalUpdateRequestType): Promise<void> =>
    vendorsAxios.put(`vendors/users/proposals/${proposalId}`, payload)

  const getRequestForProposalPdf = (requestForProposalId: string): Promise<AxiosResponse<string>> =>
    vendorsAxios.get(`vendors/users/request-for-proposals/${requestForProposalId}/pdf`, { responseType: 'blob' })

  const getProposalPdf = (proposalId: string): Promise<AxiosResponse<string>> =>
    vendorsAxios.get(`vendors/users/proposals/${proposalId}/pdf`, { responseType: 'blob' })

  const getCompanyByBusinessId = (businessId: string, region: string): Promise<CompanyResponseType> =>
    vendorsAxios
      .get(`/companies/${businessId}`, {
        params: {
          region
        }
      })
      .then((res) => res.data)

  return {
    getWorkRegions,
    checkRepresentationRights,
    ensurementRequest,
    getMe,
    getVendorDetailsByBusinessId,
    getVendorDetailsById,
    getIsVendorFlowCompleted,
    completeVendorFlow,
    getVendorContactsById,
    updateUserDetails,
    addUserContact,
    addGeneralContact,
    getServiceClassifications,
    userContactReceivesRfps,
    userContactDoesNotReceiveRfps,
    getUserContact,
    generalContactReceivesRfps,
    generalContactDoesNotReceiveRfps,
    updateUserContact,
    getVendorGeneralContact,
    deleteUserContact,
    deleteGeneralContact,
    addVendor,
    updateGeneralContact,
    getWorkRegionsByVendorId,
    setWorkRegionForVendorId,
    deleteWorkRegionFromVendorId,
    getUserVendors,
    setServiceClassificationForVendorId,
    deleteServiceClassificationForVendorId,
    getServiceClassificationsByVendorId,
    getHistoryTimelineByVendorId,
    getOpenRequestForProposals,
    getAvailableToModifyRequestForProposals,
    getUserProposalsHistory,
    updateVendorBasicDetails,
    getProposalById,
    updateProposal,
    getRequestForProposalPdf,
    getProposalPdf,
    getCompanyByBusinessId
  }
}
